'use client'

import { useEffect, useRef } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { toast } from '@/components/ui/toast'
import { canAccess, homeFor } from '@/lib/roles'

/**
 * Client-side route guard (Rule 12 §3). When the signed-in roles may not open
 * the current route (e.g. an AP-Operator typing /ai/policies), redirect to the
 * session's home (`homeFor`: `/review` for reviewer-only users) with a toast;
 * a blocked `/` (the post-login default) redirects silently. The API is enforced independently by the backend
 * authz map; this only keeps the UI from rendering dead pages.
 */
export function RoleGuard({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const router = useRouter()
  const { data: session, status } = useSession()
  const roles = session?.roles
  const lastBlocked = useRef<string | null>(null)

  const allowed = status !== 'authenticated' || canAccess(pathname ?? '/', roles)

  useEffect(() => {
    if (allowed) {
      lastBlocked.current = null
      return
    }
    if (lastBlocked.current === pathname) return
    lastBlocked.current = pathname
    if (pathname !== '/') {
      toast.warning('That page is not available for your role.', {
        description: 'You have been returned to your home page.',
      })
    }
    router.replace(homeFor(roles))
  }, [allowed, pathname, router, roles])

  if (!allowed) return null
  return <>{children}</>
}
